(() => {
  const initCartRelatedProducts = (root = document) => {
    if (!root.querySelector('[data-cart-related-swiper]')) return;
    if (typeof Swiper === 'undefined') {
      window.loadSwiper().then(() => initCartRelatedProducts(root));
      return;
    }

    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    root.querySelectorAll('[data-cart-related-swiper]').forEach((swiperEl) => {
      if (swiperEl.swiper || swiperEl.dataset.swiperInitialized === 'true') return;

      const rail = swiperEl.closest('[data-cart-related-products]');
      if (!rail) return;

      swiperEl.dataset.swiperInitialized = 'true';

      if (isMobile) {
        // Swiper's JS touch handling fights the page's native scroll on mobile;
        // skip Swiper entirely and let the browser scroll the rail natively instead.
        swiperEl.classList.add('cart-related-products__swiper--native-scroll');
        return;
      }

      new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 4,
        watchOverflow: true,
        slidesOffsetAfter: 16,
        breakpoints: { 1024: { slidesOffsetAfter: 0 } },
        freeMode: { enabled: true, sticky: false },
        scrollbar: { el: rail.querySelector('.cart-related-products__scrollbar'), draggable: true },
        mousewheel: { forceToAxis: true, releaseOnEdges: true },
      });
    });
  };

  const getSectionInnerHTML = (html, selector) => {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  };

  const getCartItemsContext = (button) => {
    const inDrawer = button.closest('cart-drawer') || button.closest('#CartDrawer');
    return inDrawer ? document.querySelector('cart-drawer-items') : document.querySelector('cart-items');
  };

  const getDrawerSectionId = () =>
    document.getElementById('cart-drawer-section')?.dataset.sectionId || 'cart-drawer';

  const addRelatedProductToCart = (button) => {
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('is-loading')) return;

    const cartItems = getCartItemsContext(button);

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    if (cartItems) {
      formData.append('sections', cartItems.getSectionsToRender().map((s) => s.section));
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-related-products',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        }

        if (!cartItems || !response.sections) {
          window.location = window.routes.cart_url;
          return;
        }

        cartItems.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          const elementToReplace = container?.querySelector(section.selector) || container;
          if (!elementToReplace || !response.sections[section.section]) return;
          const newHTML = getSectionInnerHTML(response.sections[section.section], section.selector);
          if (newHTML === null) return;
          elementToReplace.innerHTML = newHTML;
          initCartRelatedProducts(elementToReplace);
        });

        // Refresh recommendations outside cart-drawer-items (drawer context)
        const drawerScroll = document.querySelector('.drawer__scroll');
        const drawerSectionId = getDrawerSectionId();
        if (drawerScroll && response.sections[drawerSectionId]) {
          const parsed = new DOMParser().parseFromString(response.sections[drawerSectionId], 'text/html');
          const newRec = parsed.querySelector('[data-cart-related-products]');
          const oldRec = drawerScroll.querySelector('[data-cart-related-products]');
          if (oldRec) oldRec.remove();
          const cartDrawerItems = drawerScroll.querySelector('cart-drawer-items');
          if (newRec && cartDrawerItems) {
            cartDrawerItems.insertAdjacentElement('afterend', newRec);
            initCartRelatedProducts(newRec);
          }
        }

        return fetch(`${routes.cart_url}.js`)
          .then((r) => r.json())
          .then((cartData) => {
            document.querySelectorAll('.cart-item__count').forEach((el) => {
              el.textContent = cartData.item_count;
            });

            document.querySelectorAll('.cart__checkout-total').forEach((el) => {
              const amount = cartData.total_price / 100;
              const format = window.Shopify?.moneyFormat || '{{amount}}';
              const plain = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
              const withComma = plain.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              const noDecimals = String(Math.round(amount));
              const noDecimalsComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              el.textContent = format
                .replace('{{amount_with_comma_separator}}', withComma)
                .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsComma)
                .replace('{{amount_no_decimals}}', noDecimals)
                .replace('{{amount}}', plain);
            });

            // Do not publish cartUpdate — triggers onCartUpdate() which re-fetches and wipes recommendations.
            // Cart icon bubble updates via getSectionsToRender() above.
            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { source: 'cart-related-products', cartData, variantId },
            }));
          });
      })
      .catch(console.error)
      .finally(() => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-related-add]');
    if (!button) return;
    event.preventDefault();
    addRelatedProductToCart(button);
  });

  document.addEventListener('DOMContentLoaded', () => initCartRelatedProducts());
  window.addEventListener('load', () => initCartRelatedProducts());
  document.addEventListener('shopify:section:load', (event) => initCartRelatedProducts(event.target));
  document.addEventListener('cart:updated', () => initCartRelatedProducts());

  ['cart-items', 'cart-drawer-items'].forEach((selector) => {
    const el = document.querySelector(selector);
    if (el) new MutationObserver(() => initCartRelatedProducts(el)).observe(el, { childList: true, subtree: true });
  });
})();
